import { getCurrency } from './currency.js';
export function money(amount, currency) {
    if (!Number.isInteger(amount)) {
        throw new TypeError(`Un montant doit être un entier dans la plus petite unité de la devise (reçu : ${amount}). ` +
            `Utilisez fromDecimal() pour convertir une valeur décimale.`);
    }
    if (!Number.isSafeInteger(amount)) {
        throw new RangeError(`Montant hors des entiers sûrs : ${amount}`);
    }
    getCurrency(currency);
    return { amount, currency };
}
export function zero(currency) {
    return { amount: 0, currency };
}
function assertSameCurrency(a, b) {
    if (a.currency !== b.currency) {
        throw new TypeError(`Opération entre devises différentes : ${a.currency} et ${b.currency}. ` +
            `Une conversion doit être explicite.`);
    }
}
export function add(a, b) {
    assertSameCurrency(a, b);
    return money(a.amount + b.amount, a.currency);
}
export function subtract(a, b) {
    assertSameCurrency(a, b);
    return money(a.amount - b.amount, a.currency);
}
export function sum(items, currency) {
    return items.reduce((acc, m) => add(acc, m), zero(currency));
}
export function negate(m) {
    return money(-m.amount, m.currency);
}
export function isZero(m) {
    return m.amount === 0;
}
export function isNegative(m) {
    return m.amount < 0;
}
export function compare(a, b) {
    assertSameCurrency(a, b);
    return a.amount === b.amount ? 0 : a.amount < b.amount ? -1 : 1;
}
export function equals(a, b) {
    return a.currency === b.currency && a.amount === b.amount;
}
/**
 * Arrondi au demi-supérieur symétrique (half-up away from zero).
 *
 * Choisi plutôt que l'arrondi bancaire : c'est celui qu'attend un commerçant
 * et celui qu'appliquent la plupart des administrations fiscales.
 * -0,5 s'arrondit à -1, pas à 0 — la symétrie garantit que
 * multiply(negate(m), r) === negate(multiply(m, r)).
 *
 * ⚠️ La correction de précision n'est pas cosmétique. Un taux comme 1.005 n'est
 * pas représentable exactement en binaire : 100 × 1.005 vaut 100.49999999999999,
 * que Math.round ramène à 100 au lieu de 101. Ramener la valeur à 15 chiffres
 * significatifs — largement sous les 2^53 entiers sûrs — élimine l'artefact de
 * représentation sans jamais modifier une valeur légitime.
 */
function roundHalfUp(value) {
    const corrected = Number(value.toPrecision(15));
    return corrected < 0 ? -Math.round(-corrected) : Math.round(corrected);
}
/** Multiplie par un facteur réel (quantité, taux) puis arrondit. */
export function multiply(m, factor) {
    if (!Number.isFinite(factor)) {
        throw new TypeError(`Facteur invalide : ${factor}`);
    }
    return money(roundHalfUp(m.amount * factor), m.currency);
}
/** Applique un pourcentage (19.25 pour 19,25 %). */
export function percentage(m, rate) {
    return multiply(m, rate / 100);
}
/**
 * Répartit un montant en N parts sans perdre le moindre centime.
 * Les unités restantes sont distribuées une à une sur les premières parts.
 * allocate(money(100, 'EUR'), 3) → [34, 33, 33] et non [33.33, 33.33, 33.33].
 */
export function allocate(m, parts) {
    if (!Number.isInteger(parts) || parts <= 0) {
        throw new RangeError(`Nombre de parts invalide : ${parts}`);
    }
    const base = Math.trunc(m.amount / parts);
    let remainder = m.amount - base * parts;
    const step = remainder < 0 ? -1 : 1;
    return Array.from({ length: parts }, () => {
        let value = base;
        if (remainder !== 0) {
            value += step;
            remainder -= step;
        }
        return money(value, m.currency);
    });
}
/** Convertit une valeur décimale saisie par un humain (12.34 EUR → 1234). */
export function fromDecimal(value, currency) {
    const { decimals } = getCurrency(currency);
    return money(roundHalfUp(value * 10 ** decimals), currency);
}
/** Valeur décimale, pour l'affichage uniquement — jamais pour un calcul. */
export function toDecimal(m) {
    const { decimals } = getCurrency(m.currency);
    return m.amount / 10 ** decimals;
}
/** Formatage localisé. `locale` suit le pays de la boutique (fr-CM, fr-HT…). */
export function format(m, locale = 'fr-FR') {
    const { decimals } = getCurrency(m.currency);
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: m.currency,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(toDecimal(m));
}
//# sourceMappingURL=money.js.map